import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  clientAPI, categoryAPI, techniqueAPI, quoteAPI, settingsAPI,
  testimonialAPI, emailTemplateAPI, delayAPI, popupAPI, sizeAPI, servicesAPI
} from '../../services/api';
import {
  Button, Modal, FormField, Input, Textarea, Select,
  ConfirmDialog, EmptyState, Pagination, PasswordInput
} from '../../components/common';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';

/* ─── Icônes SVG réutilisables (style Lucide) ─── */
const _i = (c, s = 14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{c}</svg>;
const ICO = {
  edit:    _i(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>),
  trash:   _i(<><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>),
  send:    _i(<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>),
  eye:     _i(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>),
  eyeOff:  _i(<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>),
  mail:    _i(<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></>),
  refresh: _i(<><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>),
  info:    _i(<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>),
  warning: _i(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>),
};

/* ═══════════════════════════════════════════════════════════
   UTILITAIRE — Parse CSV / Excel basique
   ═══════════════════════════════════════════════════════════ */
const CSV_HEADER_MAP = {
  'entreprise':'company','société':'company','societe':'company','company':'company','raison sociale':'company','raison_sociale':'company',
  'prénom':'first_name','prenom':'first_name','firstname':'first_name','first name':'first_name','first_name':'first_name',
  'nom':'last_name','lastname':'last_name','last name':'last_name','last_name':'last_name',
  'nom contact':'contact_name','contact':'contact_name','contact_name':'contact_name','nom complet':'contact_name',
  'email':'email','e-mail':'email','mail':'email','courriel':'email',
  'téléphone':'phone','telephone':'phone','phone':'phone','tél':'phone','tel':'phone','mobile':'phone','portable':'phone',
  'adresse':'address','address':'address',
  'ville':'city','city':'city',
  'code postal':'zip_code','code_postal':'zip_code','codepostal':'zip_code','cp':'zip_code',
  'zip':'zip_code','zip code':'zip_code','zip_code':'zip_code','postal code':'zip_code','postalcode':'zip_code','postal_code':'zip_code','code zip':'zip_code',
  'pays':'country','country':'country',
  'siret':'siret',
  'tva':'vat_number','vat':'vat_number','vat_number':'vat_number',
  'notes':'notes','note':'notes','remarques':'notes','commentaires':'notes',
};

const normalizeCSVHeader = (h) =>
  h.replace(/\uFEFF/g, '')
   .trim()
   .replace(/^"|"$/g, '')
   .toLowerCase()
   .replace(/[\u00A0\u00AD\u202F\u205F\u2000-\u200B\u3000]/g, ' ')
   .replace(/\s+/g, ' ')
   .trim()
const parseCSV = (text) => {
  const clean = text.replace(/﻿/g, '').trim();
  const lines = clean.split(/\r?\n/);
  if (lines.length < 2) return [];
  const sep = lines[0].includes(';') ? ';' : lines[0].includes('	') ? '	' : ',';
  const headers = lines[0].split(sep).map(h => {
    const norm = normalizeCSVHeader(h);
    return CSV_HEADER_MAP[norm] !== undefined ? CSV_HEADER_MAP[norm] : norm;
  });
  return lines.slice(1)
    .filter(l => l.trim())
    .map(line => {
      const vals = line.split(sep).map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((h, i) => { if (vals[i]) obj[h] = vals[i]; });
      return obj;
    })
    .filter(row => row.company || row.contact_name || row.first_name || row.email);
};
const exportToCSV = (data, filename) => {
  const headers = ['company','first_name','last_name','email','phone','address','city','zip_code','country','siret','vat_number','notes'];
  const labels  = ['Entreprise','Prénom','Nom','Email','Téléphone','Adresse','Ville','Code postal','Pays','SIRET','TVA','Notes'];
  const rows = [labels.join(';'), ...data.map(c => headers.map(h => `"${(c[h] || '').replace(/"/g, '""')}"`).join(';'))];
  const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ═══════════════════════════════════════════════════════════
   MODAL ENVOI EMAIL CLIENT
   ═══════════════════════════════════════════════════════════ */
const ClientSendEmailModal = ({ isOpen, onClose, client }) => {
  const [templates, setTemplates] = useState([]);
  const [tplId,     setTplId]     = useState('');
  const [vars,      setVars]      = useState({});
  const [sending,   setSending]   = useState(false);
  const selectedTpl = templates.find(t => String(t.id) === String(tplId));

  useEffect(() => {
    if (!isOpen) return;
    emailTemplateAPI.list()
      .then(({ data }) => {
        const all = data.data || [];
        // Priorité aux templates catégorie "client", fallback sur tous si aucun
        const clientTpls = all.filter(t => t.is_active && t.category === 'client');
        setTemplates(clientTpls.length > 0 ? clientTpls : all.filter(t => t.is_active));
        setTplId(''); setVars({});
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTpl || !client) return;
    const auto = {};
    (selectedTpl.variables || []).forEach(v => {
      if      (v === 'client_name')    auto[v] = client.first_name ? `${client.first_name} ${client.last_name || ''}`.trim() : (client.contact_name || client.company || '');
      else if (v === 'client_email')   auto[v] = client.email || '';
      else if (v === 'company')        auto[v] = client.company || '';
      else if (v === 'client_phone')   auto[v] = client.phone || '';
      else if (v === 'site_name')      auto[v] = 'Artimarq';
    });
    setVars(auto);
  }, [tplId, selectedTpl, client]);

  const send = async () => {
    if (!tplId)            { toast.error('Choisissez un template'); return; }
    if (!client?.email)    { toast.error('Ce client n\'a pas d\'email'); return; }
    setSending(true);
    try {
      await emailTemplateAPI.send(parseInt(tplId), { recipients: [client.email], variables: vars });
      toast.success('Email envoyé ✓');
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur envoi'); }
    finally { setSending(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Envoyer un email à ${client?.first_name || client?.company || ''}`} size="lg"
      footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="primary" loading={sending} onClick={send}>Envoyer</Button>
      </div>}
    >
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{padding:'10px 14px',background:'rgba(59,130,246,.05)',border:'1px solid rgba(59,130,246,.15)',borderRadius:8,fontSize:13}}>
          Destinataire : <strong>{client?.email}</strong>
        </div>
        <FormField label="Template email" required>
          <Select value={tplId} onChange={e => setTplId(e.target.value)}>
            <option value="">-- Choisir un template --</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
          </Select>
        </FormField>
        {selectedTpl && (selectedTpl.variables || []).length > 0 && (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div style={{fontSize:12,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase'}}>Variables du template</div>
            {(selectedTpl.variables || []).map(v => (
              <FormField key={v} label={`{{${v}}}`}>
                <Input value={vars[v] || ''} onChange={e => setVars(p => ({...p,[v]:e.target.value}))} placeholder={`Valeur pour ${v}`} />
              </FormField>
            ))}
          </div>
        )}
        {selectedTpl && (
          <div style={{padding:'10px 14px',background:'var(--bg-page)',borderRadius:8,fontSize:13}}>
            <div style={{fontWeight:700,marginBottom:4}}>Sujet : {selectedTpl.subject}</div>
            {selectedTpl.body && <div style={{color:'var(--text-muted)',fontSize:11,fontFamily:'monospace',whiteSpace:'pre-wrap',maxHeight:120,overflow:'auto'}}>{selectedTpl.body.replace(/<[^>]+>/g,'').slice(0,300)}{selectedTpl.body.length > 300 ? '…' : ''}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   CLIENTS — avec Import / Export CSV
   ═══════════════════════════════════════════════════════════ */
export const AdminClientsEnhancedPage = () => {
  const [clients,   setClients]   = useState([]);
  const [meta,      setMeta]      = useState(null);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [confirm,   setConfirm]   = useState(null);
  const [importModal,setImportModal] = useState(false);
  const [importData, setImportData]  = useState([]);
  const [importing,  setImporting]   = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [emailModal,  setEmailModal]  = useState(false);
  const [emailClient, setEmailClient] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const importRef = useRef();

  const emptyForm = { company:'', first_name:'', last_name:'', client_type:'professional', email:'', phone:'', address:'', city:'', zip_code:'', country:'FR', siret:'', vat_number:'', notes:'' };
  const [form, setForm] = useState(emptyForm);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    clientAPI.list({ page, per_page: 16, search })
      .then(({ data }) => { setClients(data.data || []); setMeta(data.meta); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setEditing(null); setForm(emptyForm); setEditModal(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setEditModal(true); };

  const save = async () => {
    if (!form.email) { toast.error('Email requis'); return; }
    if (!form.email.includes('@')) { toast.error('Email invalide'); return; }
    setSaving(true);
    try {
      if (editing) await clientAPI.update(editing.id, form);
      else         await clientAPI.create(form);
      toast.success(editing ? 'Client mis à jour ✓' : 'Client créé ✓');
      setEditModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await clientAPI.delete(id); toast.success('Client supprimé'); load(); }
    catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  // ── Export ──
  const handleExport = async () => {
    try {
      const params = { per_page: 10000 };
      if (search) params.search = search;
      const { data } = await clientAPI.list(params);
      exportToCSV(data.data || [], `clients_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`${data.data?.length || 0} clients exportés ✓`);
    } catch { toast.error('Erreur export'); }
  };

  // ── Import ──
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(ev.target.result);
        setImportData(parsed);
        setImportErrors([]);
        setImportModal(true);
      } catch { toast.error('Fichier invalide'); }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const runImport = async () => {
    if (!importData.length) return;
    setImporting(true);
    let ok = 0, fail = 0;
    const errs = [];
    for (const row of importData) {
      try {
        const payload = { ...row, country: row.country || 'FR' };
        // Compatibilité anciens CSV avec colonne "Nom contact" unique
        if (!payload.first_name && !payload.last_name && payload.contact_name) {
          const parts = payload.contact_name.trim().split(' ');
          payload.first_name = parts[0] || '';
          payload.last_name  = parts.slice(1).join(' ') || '';
        }
        await clientAPI.create(payload);
        ok++;
      }
      catch (err) { fail++; errs.push(`${row.company || row.email}: ${err.response?.data?.message || 'Erreur'}`); }
    }
    setImportErrors(errs);
    setImporting(false);
    if (ok) { toast.success(`${ok} client(s) importé(s) ✓`); load(); }
    if (fail) toast.error(`${fail} erreur(s)`);
    if (!fail) setImportModal(false);
  };

  const COUNTRIES = [['FR','France'],['BE','Belgique'],['CH','Suisse'],['LU','Luxembourg'],['DE','Allemagne'],['ES','Espagne'],['IT','Italie'],['GB','Royaume-Uni'],['US','États-Unis']];

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelected = (list) => list.length > 0 && list.every(i => selectedIds.has(i.id));
  const toggleAll = (list) => setSelectedIds(isAllSelected(list) ? new Set() : new Set(list.map(i => i.id)));
  const bulkDelete = async () => {
    let ok = 0;
    for (const id of selectedIds) { try { await clientAPI.delete(id); ok++; } catch {} }
    toast.success(`${ok} élément(s) supprimé(s)`);
    setSelectedIds(new Set()); setBulkConfirm(false); load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Clients</h1><p className="admin-page-subtitle">{meta?.total || 0} clients enregistrés</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={handleExport} title="Exporter en CSV">⬇ Export CSV</Button>
          <label className="btn btn-secondary btn-md" style={{ cursor: 'pointer' }} title="Importer depuis CSV/Excel">
            ⬆ Import CSV
            <input ref={importRef} type="file" accept=".csv,.txt,.xlsx" onChange={handleImportFile} hidden />
          </label>
          <Button variant="primary" onClick={openNew}>+ Nouveau client</Button>
        </div>
      </div>

      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="search-bar" style={{ maxWidth: 320 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Nom, email, entreprise, SIRET…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft: 36 }} />
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
              {ICO.info} Import CSV : colonnes acceptées — Entreprise, Prénom, Nom, Email, Téléphone, Adresse, Ville, Code postal, SIRET
            </span>
          </div>

          {loading ? <div className="page-loader"><div className="spinner" /></div> :
            !clients.length ? <EmptyState icon={_i(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>, 40)} title="Aucun client" action={<Button variant="primary" onClick={openNew}>Ajouter un client</Button>} /> :
            <>
              {selectedIds.size > 0 && (
                <div style={{display:'flex',alignItems:'center',gap:12,padding:'8px 16px',background:'rgba(230,57,70,.06)',borderRadius:'var(--radius-sm)',border:'1px solid rgba(230,57,70,.2)',marginBottom:'var(--space-sm)'}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--brand-accent)'}}>{selectedIds.size} sélectionné(s)</span>
                  <button style={{marginLeft:'auto',fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}} onClick={()=>setSelectedIds(new Set())}>Tout désélectionner</button>
                  <Button size="sm" variant="ghost" onClick={()=>setBulkConfirm(true)} style={{color:'var(--error)'}}>🗑 Supprimer ({selectedIds.size})</Button>
                </div>
              )}
            <table className="data-table">
              <thead><tr><th style={{width:36}}><input type="checkbox" checked={isAllSelected(clients)} onChange={()=>toggleAll(clients)} /></th><th>Type</th><th>Entreprise</th><th>Prénom</th><th>Nom</th><th>Email</th><th>Téléphone</th><th>Ville</th><th>Solde</th><th>Actions</th></tr></thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td><input type="checkbox" checked={selectedIds.has(c.id)} onChange={()=>toggleSelect(c.id)} /></td>
                    <td>
                      <span className={`badge ${c.client_type === 'professional' ? 'badge-info' : 'badge-neutral'}`}>
                        {c.client_type === 'professional' ? 'Pro' : 'Particulier'}
                      </span>
                    </td>
                    <td><div style={{ fontWeight: 700, fontSize: 14 }}>{c.company || '—'}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.siret ? `SIRET: ${c.siret}` : ''}</div></td>
                    <td style={{ fontSize: 13 }}>{c.first_name || c.contact_name?.split(' ')[0] || '—'}</td>
                    <td style={{ fontSize: 13 }}>{c.last_name || c.contact_name?.split(' ').slice(1).join(' ') || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}><a href={`mailto:${c.email}`} style={{ color: 'var(--brand-accent)' }}>{c.email}</a></td>
                    <td style={{ fontSize: 13 }}>{c.phone || '—'}</td>
                    <td style={{ fontSize: 13 }}>{c.city || '—'}</td>
                    <td style={{ fontWeight: 700, color: parseFloat(c.balance || 0) >= 0 ? 'var(--success)' : 'var(--error)' }}>
                      {parseFloat(c.balance || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(c)}>{ICO.edit}</Button>
                        {c.email && <Button size="sm" variant="secondary" onClick={() => { setEmailClient(c); setEmailModal(true); }} title="Envoyer un email">{ICO.send}</Button>}
                        <Button size="sm" variant="ghost" onClick={() => setConfirm(c.id)}>{ICO.trash}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          }
          <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border)' }}>
            <Pagination meta={meta} onPageChange={setPage} />
          </div>
        </div>
      </div>

      {/* ── Modal client ── */}
      <Modal isOpen={editModal} onClose={() => setEditModal(false)} title={editing ? `Modifier : ${editing.company || editing.first_name + ' ' + editing.last_name}` : 'Nouveau client'} size="lg"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setEditModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button></div>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>

          {/* Type de client */}
          <div style={{ gridColumn: '1/-1' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Type de client</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {[['professional','Professionnel / Entreprise'],['individual','Particulier']].map(([val, label]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', border: `1.5px solid ${form.client_type === val ? 'var(--brand-accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', cursor: 'pointer', background: form.client_type === val ? 'rgba(230,57,70,.04)' : 'var(--bg-card)', flex: 1, transition: 'all .15s' }}>
                  <input type="radio" name="client_type" value={val} checked={form.client_type === val} onChange={() => f('client_type', val)} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: form.client_type === val ? 'var(--brand-accent)' : 'var(--text-secondary)' }}>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Entreprise — facultatif */}
          <div style={{ gridColumn: '1/-1' }}>
            <FormField label="Entreprise / Raison sociale" hint="Facultatif pour les particuliers">
              <Input value={form.company} onChange={e => f('company', e.target.value)} placeholder="Mon Entreprise SAS" />
            </FormField>
          </div>

          {/* Prénom / Nom séparés — facultatifs */}
          <FormField label="Prénom" hint="Facultatif">
            <Input value={form.first_name} onChange={e => f('first_name', e.target.value)} placeholder="Jean" />
          </FormField>
          <FormField label="Nom" hint="Facultatif">
            <Input value={form.last_name} onChange={e => f('last_name', e.target.value)} placeholder="Dupont" />
          </FormField>

          {/* Email — seul champ obligatoire */}
          <FormField label="Email" required>
            <Input type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="contact@entreprise.fr" />
          </FormField>
          <FormField label="Téléphone">
            <Input value={form.phone || ''} onChange={e => f('phone', e.target.value)} placeholder="+33 1 23 45 67 89" />
          </FormField>

          <div style={{ gridColumn: '1/-1' }}>
            <FormField label="Adresse">
              <Input value={form.address || ''} onChange={e => f('address', e.target.value)} placeholder="12 rue de la Paix" />
            </FormField>
          </div>
          <FormField label="Ville">
            <Input value={form.city || ''} onChange={e => f('city', e.target.value)} placeholder="Paris" />
          </FormField>
          <FormField label="Code postal">
            <Input value={form.zip_code || ''} onChange={e => f('zip_code', e.target.value)} placeholder="75001" />
          </FormField>
          <FormField label="Pays">
            <Select value={form.country || 'FR'} onChange={e => f('country', e.target.value)}>
              {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </FormField>
          <FormField label="SIRET">
            <Input value={form.siret || ''} onChange={e => f('siret', e.target.value)} placeholder="123 456 789 00012" />
          </FormField>
          <FormField label="N° TVA intracommunautaire">
            <Input value={form.vat_number || ''} onChange={e => f('vat_number', e.target.value)} placeholder="FR12345678901" />
          </FormField>
          <div style={{ gridColumn: '1/-1' }}>
            <FormField label="Notes internes">
              <Textarea rows={3} value={form.notes || ''} onChange={e => f('notes', e.target.value)} />
            </FormField>
          </div>
        </div>
      </Modal>

      {/* ── Modal import ── */}
      <Modal isOpen={importModal} onClose={() => setImportModal(false)} title="Import clients" size="lg"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setImportModal(false)}>Annuler</Button>
          <Button variant="primary" loading={importing} onClick={runImport}>Importer {importData.length} client(s)</Button>
        </div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <div style={{ padding: 12, background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--info)' }}>
            <strong>{importData.length} lignes détectées</strong> — Vérifiez l'aperçu avant d'importer.
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Entreprise</th><th>Prénom</th><th>Nom</th><th>Email</th><th>Téléphone</th><th>Ville</th></tr></thead>
              <tbody>
                {importData.slice(0, 50).map((row, i) => (
                  <tr key={i}>
                    <td>{row.company || '—'}</td>
                    <td>{row.first_name || (row.contact_name?.split(' ')[0]) || '—'}</td>
                    <td>{row.last_name || (row.contact_name?.split(' ').slice(1).join(' ')) || '—'}</td>
                    <td>{row.email || <span style={{ color: 'var(--error)' }}>⚠ manquant</span>}</td>
                    <td>{row.phone || '—'}</td>
                    <td>{row.city || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {importData.length > 50 && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>… et {importData.length - 50} autres lignes</p>}
          {importErrors.length > 0 && (
            <div style={{ padding: 12, background: 'var(--error-bg)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--error)' }}>
              <strong>Erreurs :</strong>
              <ul style={{ marginTop: 6, paddingLeft: 16 }}>
                {importErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer le client" message="Le client et toutes ses données associées seront supprimés définitivement." confirmLabel="Supprimer" danger />
      <ConfirmDialog isOpen={bulkConfirm} onClose={() => setBulkConfirm(false)} onConfirm={bulkDelete} title="Supprimer la sélection" message={`${selectedIds.size} client(s) seront définitivement supprimés.`} confirmLabel="Supprimer tout" danger />
      <ClientSendEmailModal isOpen={emailModal} onClose={() => { setEmailModal(false); setEmailClient(null); }} client={emailClient} />
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   DEVIS — création directe depuis le backoffice
   ═══════════════════════════════════════════════════════════ */
export const AdminQuotesEnhancedPage = () => {
  const [quotes,   setQuotes]   = useState([]);
  const [clients,  setClients]  = useState([]);
  const [meta,     setMeta]     = useState(null);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [confirm,     setConfirm]    = useState(null);
  const [saving,      setSaving]     = useState(false);
  const [selectedIds, setSelectedIds]= useState(new Set());
  const [bulkConfirm, setBulkConfirm]= useState(false);

  const vatRate = 20;
  const emptyLine = { description: '', quantity: 1, unit_price: 0, discount: 0 };
  const emptyForm = {
    client_id: '', client_snapshot: {}, items: [{ ...emptyLine }],
    discount_pct: 0, tax_rate: vatRate, notes: '', production_delay: '',
    valid_until: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    status: 'pending'
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    const p = { page, per_page: 16 };
    if (search) p.search = search;
    if (status) p.status = status;
    Promise.all([quoteAPI.list(p), clientAPI.list({ per_page: 200 })])
      .then(([q, c]) => { setQuotes(q.data.data || []); setMeta(q.data.meta); setClients(c.data.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  // Calculs dynamiques
  const computeTotals = (items, discPct, taxRate) => {
    const sub  = items.reduce((s, i) => s + (parseFloat(i.quantity)||0) * (parseFloat(i.unit_price)||0) * (1 - (parseFloat(i.discount)||0)/100), 0);
    const disc = sub * (parseFloat(discPct)||0) / 100;
    const tax  = (sub - disc) * (parseFloat(taxRate)||0) / 100;
    return { subtotal: sub - disc, discount: disc, tax, total: sub - disc + tax };
  };
  const totals = computeTotals(form.items, form.discount_pct, form.tax_rate);

  const addLine    = () => setForm(p => ({ ...p, items: [...p.items, { ...emptyLine }] }));
  const removeLine = (i) => setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, k, v) => setForm(p => ({ ...p, items: p.items.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const selectClient = (id) => {
    const c = clients.find(x => x.id === parseInt(id));
    setForm(p => ({
      ...p, client_id: id,
      client_snapshot: c ? {
        company: c.company,
        first_name: c.first_name || c.contact_name?.split(' ')[0] || '',
        last_name: c.last_name || c.contact_name?.split(' ').slice(1).join(' ') || '',
        email: c.email, phone: c.phone,
        address: c.address, city: c.city,
        zip_code: c.zip_code, country: c.country,
        siret: c.siret, vat_number: c.vat_number
      } : {}
    }));
  };

  const save = async () => {
    if (!form.items.length) { toast.error('Ajoutez au moins une ligne'); return; }
    setSaving(true);
    try {
      await quoteAPI.create({ ...form, ...computeTotals(form.items, form.discount_pct, form.tax_rate) });
      toast.success('Devis créé ✓');
      setModal(false); setForm(emptyForm); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => { try { await quoteAPI.delete(id); toast.success('Devis supprimé'); load(); } catch { toast.error('Erreur'); } setConfirm(null); };
  const toggleSelect  = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelected = (list) => list.length > 0 && list.every(i => selectedIds.has(i.id));
  const toggleAll     = (list) => setSelectedIds(isAllSelected(list) ? new Set() : new Set(list.map(i => i.id)));
  const bulkDelete    = async () => {
    let ok = 0;
    for (const id of selectedIds) { try { await quoteAPI.delete(id); ok++; } catch {} }
    toast.success(`${ok} devis supprimé(s)`);
    setSelectedIds(new Set()); setBulkConfirm(false); load();
  };
  const convert     = async (id) => { try { const { data } = await quoteAPI.convert(id); toast.success(`Facture ${data.data.reference} créée ✓`); load(); } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); } };
  const markSent    = async (id) => { try { await quoteAPI.updateStatus(id, { status: 'sent' }); toast.success('Marqué envoyé'); load(); } catch { toast.error('Erreur'); } };
  const openPdf     = (id) => window.open(quoteAPI.pdfUrl(id), '_blank');

  const statusLabels = { pending:'En attente', sent:'Envoyé', accepted:'Accepté', rejected:'Refusé', expired:'Expiré', converted:'Converti' };
  const statusBadge  = { pending:'badge-warning', sent:'badge-info', accepted:'badge-success', rejected:'badge-error', expired:'badge-neutral', converted:'badge-accent' };
  const COUNTRIES    = [['FR','France'],['BE','Belgique'],['CH','Suisse'],['LU','Luxembourg'],['DE','Allemagne'],['ES','Espagne'],['IT','Italie'],['GB','Royaume-Uni']];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Devis</h1><p className="admin-page-subtitle">{meta?.total || 0} devis</p></div>
        <Button variant="primary" onClick={() => { setForm(emptyForm); setModal(true); }}>+ Nouveau devis</Button>
      </div>

      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="search-bar" style={{ maxWidth: 280 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Référence, client…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft: 36 }} />
            </div>
            <select className="filter-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">Tous les statuts</option>
              {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading ? <div className="page-loader"><div className="spinner" /></div> :
            !quotes.length ? <EmptyState icon={_i(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>, 40)} title="Aucun devis" action={<Button variant="primary" onClick={() => { setForm(emptyForm); setModal(true); }}>Créer un devis</Button>} /> :
            <>{selectedIds.size > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'8px 16px',background:'rgba(230,57,70,.06)',borderRadius:'var(--radius-sm)',border:'1px solid rgba(230,57,70,.2)',marginBottom:'var(--space-sm)'}}>
                <span style={{fontSize:13,fontWeight:600,color:'var(--brand-accent)'}}>{selectedIds.size} sélectionné(s)</span>
                <button style={{marginLeft:'auto',fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}} onClick={()=>setSelectedIds(new Set())}>Tout désélectionner</button>
                <Button size="sm" variant="ghost" onClick={()=>setBulkConfirm(true)} style={{color:'var(--error)'}}>🗑 Supprimer ({selectedIds.size})</Button>
              </div>
            )}
            <table className="data-table">
              <thead><tr><th style={{width:36}}><input type="checkbox" checked={isAllSelected(quotes)} onChange={()=>toggleAll(quotes)} /></th><th>Référence</th><th>Client</th><th>Montant TTC</th><th>Statut</th><th>Valide jusqu'au</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {quotes.map(q => (
                  <tr key={q.id}>
                    <td><input type="checkbox" checked={selectedIds.has(q.id)} onChange={()=>toggleSelect(q.id)} /></td>
                    <td><code style={{ fontSize: 12, background: 'var(--bg-page)', padding: '2px 6px', borderRadius: 4 }}>{q.reference}</code></td>
                    <td style={{ fontWeight: 600 }}>{q.client_snapshot?.company || q.client_company || '—'}</td>
                    <td style={{ fontWeight: 700, color: 'var(--brand-accent)' }}>{parseFloat(q.total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                    <td><span className={`badge ${statusBadge[q.status] || 'badge-neutral'}`}>{statusLabels[q.status] || q.status}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{q.valid_until ? new Date(q.valid_until).toLocaleDateString('fr-FR') : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(q.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Button size="sm" variant="secondary" onClick={() => openPdf(q.id)} title="Aperçu PDF">{_i(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>)}</Button>
                        {q.status === 'accepted' && <Button size="sm" variant="primary" onClick={() => convert(q.id)} title="Convertir en facture">→ Facture</Button>}
                        {q.status === 'pending'  && <Button size="sm" variant="ghost" onClick={() => markSent(q.id)}>Envoyé</Button>}
                        <Button size="sm" variant="ghost" onClick={() => setConfirm(q.id)}>{ICO.trash}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></>
          }
          <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border)' }}>
            <Pagination meta={meta} onPageChange={setPage} />
          </div>
        </div>
      </div>

      {/* ── Modal création devis ── */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title="Nouveau devis" size="xl"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Créer le devis</Button></div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          {/* Client */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Client enregistré">
              <Select value={form.client_id} onChange={e => selectClient(e.target.value)}>
                <option value="">— Saisie manuelle —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company || ''}{c.company && (c.first_name || c.last_name) ? ' — ' : ''}{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.contact_name || c.email}</option>)}
              </Select>
            </FormField>
            <FormField label="Valide jusqu'au">
              <Input type="date" value={form.valid_until} onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))} />
            </FormField>
          </div>

          {/* Saisie manuelle client */}
          {!form.client_id && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', padding: 16, background: 'var(--bg-page)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border)' }}>
              <div style={{ gridColumn: '1/-1', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: -4 }}>COORDONNÉES CLIENT</div>
              <div style={{ gridColumn: '1/-1' }}><FormField label="Entreprise"><Input value={form.client_snapshot?.company || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, company: e.target.value } }))} placeholder="Mon Entreprise SAS" /></FormField></div>
              <FormField label="Prénom"><Input value={form.client_snapshot?.first_name || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, first_name: e.target.value } }))} placeholder="Jean" /></FormField>
              <FormField label="Nom"><Input value={form.client_snapshot?.last_name || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, last_name: e.target.value } }))} placeholder="Dupont" /></FormField>
              <FormField label="Email" required><Input type="email" value={form.client_snapshot?.email || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, email: e.target.value } }))} placeholder="contact@entreprise.fr" /></FormField>
              <FormField label="Téléphone"><Input value={form.client_snapshot?.phone || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, phone: e.target.value } }))} placeholder="+33 1 23 45 67 89" /></FormField>
              <div style={{ gridColumn: '1/-1' }}><FormField label="Adresse"><Input value={form.client_snapshot?.address || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, address: e.target.value } }))} placeholder="12 rue de la Paix" /></FormField></div>
              <FormField label="Ville"><Input value={form.client_snapshot?.city || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, city: e.target.value } }))} /></FormField>
              <FormField label="Code postal"><Input value={form.client_snapshot?.zip_code || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, zip_code: e.target.value } }))} /></FormField>
              <FormField label="Pays">
                <Select value={form.client_snapshot?.country || 'FR'} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, country: e.target.value } }))}>
                  {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </FormField>
              <FormField label="SIRET"><Input value={form.client_snapshot?.siret || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, siret: e.target.value } }))} /></FormField>
            </div>
          )}

          {/* Lignes */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Lignes du devis</div>
              <Button size="sm" variant="secondary" onClick={addLine}>+ Ajouter une ligne</Button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-page)' }}>
                  {['Description','Qté','P.U. HT (€)','Remise (%)','Total HT',''].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.items.map((line, i) => {
                  const lt = (parseFloat(line.quantity)||0) * (parseFloat(line.unit_price)||0) * (1-(parseFloat(line.discount)||0)/100);
                  return (
                    <tr key={i}>
                      <td style={{ padding: '6px 4px' }}><input className="form-input" value={line.description} onChange={e => updateLine(i,'description',e.target.value)} placeholder="Description du service…" style={{ fontSize: 13 }} /></td>
                      <td style={{ padding: '6px 4px', width: 70 }}><input className="form-input" type="number" min="1" value={line.quantity} onChange={e => updateLine(i,'quantity',e.target.value)} style={{ fontSize: 13, textAlign: 'center' }} /></td>
                      <td style={{ padding: '6px 4px', width: 110 }}><input className="form-input" type="number" step="0.01" value={line.unit_price} onChange={e => updateLine(i,'unit_price',e.target.value)} style={{ fontSize: 13, textAlign: 'right' }} /></td>
                      <td style={{ padding: '6px 4px', width: 80 }}><input className="form-input" type="number" min="0" max="100" value={line.discount} onChange={e => updateLine(i,'discount',e.target.value)} style={{ fontSize: 13, textAlign: 'center' }} /></td>
                      <td style={{ padding: '6px 10px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{lt.toFixed(2)} €</td>
                      <td>{form.items.length > 1 && <button onClick={() => removeLine(i)} style={{ color: 'var(--error)', fontSize: 20, cursor: 'pointer', background: 'none', border: 'none' }}>×</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totaux */}
          <div style={{ display: 'flex', gap: 'var(--space-xl)', alignItems: 'flex-start' }}>
            <FormField label="Notes / Conditions" style={{ flex: 1 }}>
              <Textarea rows={3} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Conditions de paiement, délai de production, notes…" />
            </FormField>
            <div style={{ minWidth: 260, background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}><span>Sous-total HT</span><strong>{totals.subtotal.toFixed(2)} €</strong></div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, marginBottom: 8, gap: 8 }}>
                <span>Remise globale (%)</span>
                <input type="number" min="0" max="100" value={form.discount_pct} onChange={e => setForm(p => ({ ...p, discount_pct: e.target.value }))} style={{ width: 60, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'center' }} />
              </div>
              {totals.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--success)', marginBottom: 8 }}><span>Remise</span><span>- {totals.discount.toFixed(2)} €</span></div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, marginBottom: 12, gap: 8 }}>
                <span>TVA (%)</span>
                <input type="number" min="0" max="100" value={form.tax_rate} onChange={e => setForm(p => ({ ...p, tax_rate: e.target.value }))} style={{ width: 60, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'center' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8, color: 'var(--text-muted)' }}><span>TVA</span><span>{totals.tax.toFixed(2)} €</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, paddingTop: 8, borderTop: '2px solid var(--border)' }}>
                <span>Total TTC</span><span style={{ color: 'var(--brand-accent)' }}>{totals.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer le devis" message="Cette action est irréversible." confirmLabel="Supprimer" danger />
      <ConfirmDialog isOpen={bulkConfirm} onClose={() => setBulkConfirm(false)} onConfirm={bulkDelete} title="Supprimer la sélection" message={`${selectedIds.size} devis seront définitivement supprimés.`} confirmLabel="Supprimer tout" danger />
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   PRODUITS — onglets Catégories + Techniques intégrés
   ═══════════════════════════════════════════════════════════ */
export const AdminCategoriesTab = () => {
  const [cats,    setCats]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const empty = { name: '', description: '', image: '', sort_order: 0, is_active: true };
  const [form, setForm] = useState(empty);

  const load = () => { setLoading(true); categoryAPI.list().then(({ data }) => setCats(data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editing) await categoryAPI.update(editing.id, form);
      else         await categoryAPI.create(form);
      toast.success(editing ? 'Mis à jour ✓' : 'Catégorie créée ✓');
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => { try { await categoryAPI.delete(id); toast.success('Supprimée'); load(); } catch { toast.error('Erreur'); } setConfirm(null); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={() => { setEditing(null); setForm(empty); setModal(true); }}>+ Nouvelle catégorie</Button>
      </div>
      {loading ? <div className="page-loader" style={{ minHeight: 200 }}><div className="spinner" /></div> :
        !cats.length ? <EmptyState icon={_i(<><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></>, 40)} title="Aucune catégorie" /> :
        <table className="data-table">
          <thead><tr><th>Nom</th><th>Slug (URL)</th><th>Description</th><th style={{ textAlign:'center' }}>Ordre</th><th style={{ textAlign:'center' }}>Statut</th><th></th></tr></thead>
          <tbody>
            {cats.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 700 }}>
                  {c.image && <img src={c.image} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4, marginRight: 8, verticalAlign: 'middle' }} onError={e => e.target.style.display='none'} />}
                  {c.name}
                </td>
                <td><code style={{ fontSize: 12, background: 'var(--bg-page)', padding: '2px 6px', borderRadius: 4, color: 'var(--text-muted)' }}>/catalogue/{c.slug}</code></td>
                <td style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 200 }}>{c.description || '—'}</td>
                <td style={{ textAlign: 'center' }}>{c.sort_order}</td>
                <td style={{ textAlign: 'center' }}><span className={`badge ${c.is_active ? 'badge-success' : 'badge-neutral'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => { setEditing(c); setForm({ name: c.name, description: c.description||'', image: c.image||'', sort_order: c.sort_order||0, is_active: !!c.is_active }); setModal(true); }}>{ICO.edit}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(c.id)}>{ICO.trash}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? `Modifier — ${editing.name}` : 'Nouvelle catégorie'}
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button></div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <FormField label="Nom" required hint="Utilisé comme titre et dans les filtres">
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="T-Shirts" />
          </FormField>
          {editing && (
            <div style={{ padding: '8px 12px', background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              URL : /catalogue/<strong>{editing.slug}</strong> &nbsp;·&nbsp; Le slug est fixe après création
            </div>
          )}
          <FormField label="Image de catégorie (URL)" hint="Optionnel — bannière ou vignette illustrant la catégorie">
            <Input value={form.image || ''} onChange={e => setForm(p => ({ ...p, image: e.target.value }))} placeholder="https://…/image.jpg" />
          </FormField>
          <FormField label="Description"><Textarea rows={2} value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Ordre d'affichage" hint="Plus petit = affiché en premier">
              <Input type="number" min="0" value={form.sort_order || 0} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
            </FormField>
            <FormField label="Statut">
              <Select value={form.is_active ? '1' : '0'} onChange={e => setForm(p => ({ ...p, is_active: e.target.value === '1' }))}>
                <option value="1">Active — visible partout</option>
                <option value="0">Inactive — masquée</option>
              </Select>
            </FormField>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer la catégorie" message="Les produits associés ne seront pas supprimés mais n'auront plus de catégorie." confirmLabel="Supprimer" danger />
    </div>
  );
};

export const AdminTechniquesTab = () => {
  const [techs,   setTechs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const empty = { name: '', description: '', image: '', min_quantity: 1, price_setup: 0, price_per_unit: 0, supports_text: true, supports_image: true, max_colors: '', sort_order: 0, is_active: true };
  const [form, setForm] = useState(empty);

  const load = () => { setLoading(true); techniqueAPI.listAll().then(({ data }) => setTechs(data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editing) await techniqueAPI.update(editing.id, form);
      else         await techniqueAPI.create(form);
      toast.success(editing ? 'Mis à jour ✓' : 'Technique créée ✓');
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const deleteTech = async () => {
    try {
      await techniqueAPI.delete(confirm);
      toast.success('Technique supprimée');
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
      setConfirm(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={() => { setEditing(null); setForm(empty); setModal(true); }}>+ Nouvelle technique</Button>
      </div>
      {loading ? <div className="page-loader" style={{ minHeight: 200 }}><div className="spinner" /></div> :
        <table className="data-table">
          <thead><tr><th>Technique</th><th>Min. qté</th><th>Setup (€)</th><th>Prix/pce (€)</th><th>Texte</th><th>Image</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {techs.map(t => (
              <tr key={t.id}>
                <td><div style={{ fontWeight: 700 }}>{t.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.description}</div></td>
                <td style={{ textAlign: 'center' }}>{t.min_quantity}</td>
                <td style={{ textAlign: 'center' }}>{parseFloat(parseFloat(t.price_setup) || 0).toFixed(2)}</td>
                <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--brand-accent)' }}>{parseFloat(parseFloat(t.price_per_unit) || 0).toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>{t.supports_text ? '✓' : '—'}</td>
                <td style={{ textAlign: 'center' }}>{t.supports_image ? '✓' : '—'}</td>
                <td><span className={`badge ${t.is_active ? 'badge-success' : 'badge-neutral'}`}>{t.is_active ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => { setEditing(t); setForm(t); setModal(true); }}>{ICO.edit}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(t.id)} title="Supprimer">🗑</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={deleteTech}
        title="Supprimer la technique"
        message="Cette technique sera définitivement supprimée. Si elle est associée à des produits, la suppression sera bloquée."
        confirmLabel="Supprimer"
        danger
      />
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Modifier la technique' : 'Nouvelle technique'} size="lg"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button></div>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          <FormField label="Nom" required><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Sérigraphie" /></FormField>
          <FormField label="Min. quantité"><Input type="number" min="1" value={form.min_quantity} onChange={e => setForm(p => ({ ...p, min_quantity: parseInt(e.target.value) || 1 }))} /></FormField>
          <FormField label="Frais de setup (€)"><Input type="number" step="0.01" value={form.price_setup} onChange={e => setForm(p => ({ ...p, price_setup: e.target.value }))} /></FormField>
          <FormField label="Prix par pièce (€)"><Input type="number" step="0.01" value={form.price_per_unit} onChange={e => setForm(p => ({ ...p, price_per_unit: e.target.value }))} /></FormField>
          <FormField label="Couleurs max (vide = illimité)"><Input type="number" min="1" value={form.max_colors || ''} onChange={e => setForm(p => ({ ...p, max_colors: e.target.value }))} placeholder="Illimité" /></FormField>
          <FormField label="Ordre d'affichage"><Input type="number" value={form.sort_order || 0} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} /></FormField>
          <div style={{ gridColumn: '1/-1' }}><FormField label="Description"><Textarea rows={2} value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormField></div>
          <div style={{ gridColumn: '1/-1' }}>
            <FormField label="Image" hint="URL d'une image illustrant la technique (JPG, PNG, WebP)">
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Input
                  value={form.image || ''}
                  onChange={e => setForm(p => ({ ...p, image: e.target.value }))}
                  placeholder="https://… ou sélectionnez dans la médiathèque"
                  style={{ flex: 1 }}
                />
                {form.image && (
                  <img
                    src={form.image}
                    alt=""
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.supports_text} onChange={e => setForm(p => ({ ...p, supports_text: e.target.checked }))} /> Supporte le texte
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.supports_image} onChange={e => setForm(p => ({ ...p, supports_image: e.target.checked }))} /> Supporte les images
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} /> Active
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   DÉLAIS DE PRODUCTION — onglet dans Produits & Catégories
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   TAILLES — onglet dans Produits & Catégories
   ═══════════════════════════════════════════════════════════ */
export const AdminSizesTab = () => {
  const [sizes,   setSizes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({ name: '', sort_order: 0, is_active: true });

  const load = () => {
    setLoading(true);
    sizeAPI.listAll()
      .then(({ data }) => setSizes(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', sort_order: 0, is_active: true }); setModal(true); };
  const openEdit   = (s) => { setEditing(s); setForm({ name: s.name, sort_order: s.sort_order, is_active: !!s.is_active }); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editing) await sizeAPI.update(editing.id, form);
      else         await sizeAPI.create(form);
      toast.success(editing ? 'Mis à jour ✓' : 'Taille créée ✓');
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await sizeAPI.delete(id); toast.success('Taille supprimée'); load(); }
    catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={openCreate}>+ Nouvelle taille</Button>
      </div>
      {loading ? <div className="page-loader" style={{ minHeight: 200 }}><div className="spinner" /></div> :
        !sizes.length ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>Aucune taille — créez-en une pour qu'elle apparaisse dans le formulaire produit.</div> :
        <table className="data-table">
          <thead><tr><th>Taille</th><th style={{ textAlign: 'center' }}>Ordre</th><th style={{ textAlign: 'center' }}>Statut</th><th></th></tr></thead>
          <tbody>
            {sizes.map(s => (
              <tr key={s.id}>
                <td style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</td>
                <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{s.sort_order}</td>
                <td style={{ textAlign: 'center' }}><span className={`badge ${s.is_active ? 'badge-success' : 'badge-neutral'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => openEdit(s)}>{ICO.edit}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(s.id)}>🗑</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Modifier la taille' : 'Nouvelle taille'}
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button></div>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          <FormField label="Nom de la taille" required hint="Ex : XS, S, M, L, XL, 38, 40, Unique…">
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="XL" autoFocus />
          </FormField>
          <FormField label="Ordre d'affichage">
            <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
          </FormField>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
              Taille active (visible dans les formulaires produit)
            </label>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer la taille" message="Cette taille sera supprimée. Les produits qui l'utilisent déjà ne sont pas affectés." confirmLabel="Supprimer" danger />
    </div>
  );
};

export const AdminDelaysTab = () => {
  const [delays,  setDelays]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);

  const empty = { label: '', days: 7, surcharge: 0, sort_order: 0, is_active: true };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    delayAPI.listAll()
      .then(({ data }) => setDelays(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit   = (d)  => { setEditing(d);   setForm({ ...d }); setModal(true); };

  const save = async () => {
    if (!form.label) { toast.error('Label requis'); return; }
    setSaving(true);
    try {
      if (editing) await delayAPI.update(editing.id, form);
      else         await delayAPI.create(form);
      toast.success(editing ? 'Mis à jour ✓' : 'Délai créé ✓');
      setModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await delayAPI.delete(id);
      toast.success('Délai supprimé');
      setConfirm(null);
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={openCreate}>+ Nouveau délai</Button>
      </div>

      {loading
        ? <div className="page-loader" style={{ minHeight: 200 }}><div className="spinner" /></div>
        : delays.length === 0
          ? <EmptyState title="Aucun délai" description="Ajoutez vos délais de production" />
          : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Label affiché</th>
                  <th style={{ textAlign: 'center' }}>Jours</th>
                  <th style={{ textAlign: 'center' }}>Surcharge (%)</th>
                  <th style={{ textAlign: 'center' }}>Ordre</th>
                  <th style={{ textAlign: 'center' }}>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {delays.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 700 }}>{d.label}</td>
                    <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{d.days} j</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: parseFloat(d.surcharge) > 0 ? 'var(--brand-accent)' : 'var(--text-secondary)' }}>
                      {parseFloat(d.surcharge || 0).toFixed(0)} %
                    </td>
                    <td style={{ textAlign: 'center' }}>{d.sort_order}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${d.is_active ? 'badge-success' : 'badge-neutral'}`}>
                        {d.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(d)}>{ICO.edit}</Button>
                        <Button size="sm" variant="danger"    onClick={() => setConfirm(d.id)}>{ICO.trash}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
      }

      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        title={editing ? 'Modifier le délai' : 'Nouveau délai de production'}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button>
            <Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button>
          </div>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          <FormField label="Label affiché au client" required hint="Ex: Standard (10-15 jours)">
            <Input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="Standard (10-15 jours)" />
          </FormField>
          <FormField label="Nombre de jours" hint="Délai moyen en jours ouvrés">
            <Input type="number" min="1" value={form.days || 7} onChange={e => setForm(p => ({ ...p, days: parseInt(e.target.value) || 1 }))} />
          </FormField>
          <FormField label="Surcharge (%)" hint="0 = pas de surcharge, 20 = +20% sur le prix">
            <Input type="number" min="0" step="1" value={form.surcharge || 0} onChange={e => setForm(p => ({ ...p, surcharge: e.target.value }))} />
          </FormField>
          <FormField label="Ordre d'affichage">
            <Input type="number" min="0" value={form.sort_order || 0} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
          </FormField>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
              Actif (visible dans le studio)
            </label>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm)}
        title="Supprimer ce délai ?"
        message="Cette action est irréversible."
        confirmLabel="Supprimer"
        variant="danger"
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   HOMEPAGE FULL SECTION — intégré dans Paramètres → Accueil
   ═══════════════════════════════════════════════════════════ */
const Toggle = ({ value, onChange }) => (
  <div
    onClick={onChange}
    style={{ width: 40, height: 22, borderRadius: 11, background: value ? 'var(--brand-accent)' : 'var(--border)', position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}
  >
    <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: value ? 21 : 3, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
  </div>
);

const HomepageFullSection = ({ settings, setSettings }) => {
  // ── Témoignages state ──
  const [testimonials,  setTestimonials]  = useState([]);
  const [loadingT,      setLoadingT]      = useState(true);
  const [tModal,        setTModal]        = useState(false);
  const [editingT,      setEditingT]      = useState(null);
  const [tForm,         setTForm]         = useState({ name:'', company:'', text:'', rating:5, avatar:'', is_active:true, sort_order:0 });
  const [savingT,       setSavingT]       = useState(false);
  const [confirmT,      setConfirmT]      = useState(null);

  // ── Techniques state ──
  const [techniques,  setTechniques]  = useState([]);
  const [loadingTech, setLoadingTech] = useState(true);
  const _ti = (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">{c}</svg>;
  const TECH_ICONS = {
    serigraphie:      _ti(<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></>),
    broderie:         _ti(<><circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></>),
    'transfert-dtf':  _ti(<><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>),
    'impression-dtg': _ti(<><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></>),
    flocage:          _ti(<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>),
    vinyle:           _ti(<><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></>),
  };

  // Section active locale (sous-navigation)
  const [section, setSection] = useState('hero');

  const sections = [
    { key: 'hero',         label: 'Hero' },
    { key: 'visibility',   label: 'Sections' },
    { key: 'techniques',   label: 'Techniques' },
    { key: 'testimonials', label: 'Avis clients' },
  ];

  useEffect(() => {
    testimonialAPI.list({ active: '' })
      .then(({ data }) => setTestimonials(data.data || []))
      .catch(() => {}).finally(() => setLoadingT(false));
    techniqueAPI.listAll()
      .then(({ data }) => setTechniques(data.data || []))
      .catch(() => {}).finally(() => setLoadingTech(false));
  }, []);

  const reloadT = () => {
    testimonialAPI.list({ active: '' }).then(({ data }) => setTestimonials(data.data || [])).catch(() => {});
  };

  const tf = (k, v) => setTForm(p => ({ ...p, [k]: v }));

  const saveTestimonial = async () => {
    if (!tForm.name || !tForm.text) { toast.error('Nom et texte requis'); return; }
    setSavingT(true);
    try {
      if (editingT) await testimonialAPI.update(editingT.id, tForm);
      else          await testimonialAPI.create(tForm);
      toast.success(editingT ? 'Avis mis à jour ✓' : 'Avis créé ✓');
      setTModal(false); reloadT();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSavingT(false); }
  };

  const deleteTestimonial = async (id) => {
    try { await testimonialAPI.delete(id); toast.success('Avis supprimé'); reloadT(); }
    catch { toast.error('Erreur suppression'); }
    setConfirmT(null);
  };

  const toggleTestimonial = async (t) => {
    try {
      await testimonialAPI.update(t.id, { ...t, is_active: !t.is_active });
      reloadT();
    } catch { toast.error('Erreur'); }
  };

  const toggleTechnique = async (t) => {
    try {
      await techniqueAPI.update(t.id, { ...t, is_active: !t.is_active });
      setTechniques(prev => prev.map(x => x.id === t.id ? { ...x, is_active: !x.is_active } : x));
      toast.success(t.is_active ? 'Technique masquée' : 'Technique affichée ✓');
    } catch { toast.error('Erreur'); }
  };

  // Helper si() — pas un composant React inline, évite la perte de focus au typing
  const si = (k, label, type = 'text', hint, placeholder) => (
    <FormField label={label} hint={hint}>
      {type === 'textarea'
        ? <Textarea value={settings[k] || ''} onChange={e => setSettings(p => ({ ...p, [k]: e.target.value }))} rows={4} placeholder={placeholder} />
        : <Input type={type} value={settings[k] || ''} onChange={e => setSettings(p => ({ ...p, [k]: e.target.value }))} placeholder={placeholder} />
      }
    </FormField>
  );

  return (
    <div style={{ display: 'flex', gap: 'var(--space-xl)', alignItems: 'flex-start' }}>
      {/* Sous-navigation verticale */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flexShrink: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 8 }}>
        {sections.map(s => (
          <button key={s.key} onClick={() => setSection(s.key)}
            style={{ padding: '9px 14px', borderRadius: 'var(--radius-sm)', textAlign: 'left', fontSize: 13, fontWeight: 600, background: section === s.key ? 'rgba(230,57,70,.07)' : 'transparent', color: section === s.key ? 'var(--brand-accent)' : 'var(--text-secondary)', border: 'none', cursor: 'pointer', transition: 'all .15s' }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Contenu de la sous-section */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>

        {/* ── Hero ── */}
        {section === 'hero' && (
          <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Section Hero — Bannière principale</h3>
            {si('hero_title', 'Titre principal', 'text', undefined, 'Personnalisez vos vêtements pro')}
            {si('hero_subtitle', 'Sous-titre', 'text', undefined, 'Du studio en ligne à la livraison')}
            {si('hero_image', 'Image de fond (URL)', 'text', 'Utilisez la Médiathèque pour obtenir l')}
            {settings.hero_image && (
              <div style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', maxHeight: 180, position: 'relative', border: '1px solid var(--border)' }}>
                <img src={settings.hero_image} alt="" style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: 'white', fontSize: 13, fontWeight: 700 }}>Aperçu image hero</span>
                </div>
                <button onClick={() => setSettings(p => ({ ...p, hero_image: '' }))}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(239,68,68,.85)', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  × Supprimer
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Sections visibles ── */}
        {section === 'visibility' && (
          <div className="admin-card" style={{ padding: 'var(--space-xl)' }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Sections affichées sur la page d'accueil</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 'var(--space-lg)' }}>Activez ou désactivez chaque section de votre page d'accueil.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['homepage_show_techniques',   'Section Techniques de marquage', 'Affiche les 6 techniques sur l\'accueil'],
                ['homepage_show_testimonials', 'Section Avis clients',            'Affiche les témoignages des clients'],
              ].map(([key, label, desc]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', border: `1.5px solid ${settings[key] === '1' ? 'var(--brand-accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', background: settings[key] === '1' ? 'rgba(230,57,70,.03)' : 'var(--bg-card)', transition: 'all .15s' }}>
                  <Toggle value={settings[key] === '1'} onChange={() => setSettings(p => ({ ...p, [key]: p[key] === '1' ? '0' : '1' }))} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{desc}</div>
                  </div>
                  <span className={`badge ${settings[key] === '1' ? 'badge-success' : 'badge-neutral'}`} style={{ marginLeft: 'auto' }}>
                    {settings[key] === '1' ? 'Visible' : 'Masqué'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Techniques ── */}
        {section === 'techniques' && (
          <div className="admin-card" style={{ padding: 'var(--space-xl)' }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Techniques affichées sur l'accueil</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 'var(--space-lg)' }}>Choisissez quelles techniques apparaissent dans la section techniques de l'accueil.</p>
            {loadingTech ? <div className="page-loader" style={{ minHeight: 120 }}><div className="spinner" /></div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {techniques.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', border: `1.5px solid ${t.is_active ? 'var(--border)' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', opacity: t.is_active ? 1 : 0.5, background: 'var(--bg-card)', transition: 'all .2s' }}>
                    <span style={{ flexShrink: 0, color: 'var(--text-secondary)' }}>{TECH_ICONS[t.slug] || _ti(<><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></>)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.description || `+${t.price_per_unit}€/pce · Min. ${t.min_quantity} pcs`}</div>
                    </div>
                    <Toggle value={!!t.is_active} onChange={() => toggleTechnique(t)} />
                    <span className={`badge ${t.is_active ? 'badge-success' : 'badge-neutral'}`}>{t.is_active ? 'Visible' : 'Masqué'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Avis clients ── */}
        {section === 'testimonials' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {/* Titre section + paramètre */}
            <div className="admin-card" style={{ padding: 'var(--space-xl)' }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 'var(--space-md)' }}>Paramètres de la section avis</h3>
              {si('homepage_testimonials_title', 'Titre affiché sur le site', 'text', undefined, 'Ils nous font confiance')}
            </div>

            {/* Liste des avis */}
            <div className="admin-card" style={{ padding: 'var(--space-xl)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
                <h3 style={{ fontWeight: 700, fontSize: 16 }}>Avis clients ({testimonials.length})</h3>
                <Button variant="primary" size="sm" onClick={() => { setEditingT(null); setTForm({ name:'', company:'', text:'', rating:5, avatar:'', is_active:true, sort_order:0 }); setTModal(true); }}>
                  + Ajouter un avis
                </Button>
              </div>

              {loadingT ? <div className="page-loader" style={{ minHeight: 120 }}><div className="spinner" /></div> :
                !testimonials.length ? (
                  <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
                    <div style={{ marginBottom: 8, color: 'var(--text-muted)' }}>{_i(<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>, 40)}</div>
                    <p>Aucun avis client. Ajoutez le premier !</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {testimonials.map(t => (
                      <div key={t.id} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: 'var(--space-md)', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', opacity: t.is_active ? 1 : 0.55, transition: 'opacity .2s' }}>
                        {/* Avatar */}
                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: t.is_active ? 'var(--brand-accent)' : 'var(--text-muted)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, flexShrink: 0, overflow: 'hidden' }}>
                          {t.avatar ? <img src={t.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : t.name[0]}
                        </div>
                        {/* Contenu */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</span>
                            {t.company && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.company}</span>}
                            <span style={{ color: '#f59e0b', fontSize: 13, letterSpacing: 1 }}>{'★'.repeat(t.rating || 5)}</span>
                            <span className={`badge ${t.is_active ? 'badge-success' : 'badge-neutral'}`} style={{ marginLeft: 'auto' }}>
                              {t.is_active ? 'Affiché' : 'Masqué'}
                            </span>
                          </div>
                          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontStyle: 'italic', margin: 0 }}>
                            "{t.text}"
                          </p>
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <Button size="sm" variant="ghost" onClick={() => toggleTestimonial(t)} title={t.is_active ? 'Masquer' : 'Afficher'}>
                            {t.is_active ? ICO.eyeOff : ICO.eye}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => { setEditingT(t); setTForm(t); setTModal(true); }}>{ICO.edit}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmT(t.id)}>{ICO.trash}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              }
            </div>
          </div>
        )}

      </div>

      {/* ── Modal avis ── */}
      {tModal && (
        <Modal isOpen={tModal} onClose={() => setTModal(false)} title={editingT ? 'Modifier l\'avis' : 'Nouvel avis client'}
          footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setTModal(false)}>Annuler</Button><Button variant="primary" loading={savingT} onClick={saveTestimonial}>Enregistrer</Button></div>}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Nom du client" required>
              <Input value={tForm.name} onChange={e => tf('name', e.target.value)} placeholder="Marie Dupont" />
            </FormField>
            <FormField label="Entreprise">
              <Input value={tForm.company || ''} onChange={e => tf('company', e.target.value)} placeholder="Mon Entreprise SAS" />
            </FormField>
            <FormField label="Note">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 0' }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => tf('rating', n)} style={{ fontSize: 22, cursor: 'pointer', background: 'none', border: 'none', color: n <= tForm.rating ? '#f59e0b' : 'var(--border)', transition: 'color .1s' }}>★</button>
                ))}
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tForm.rating}/5</span>
              </div>
            </FormField>
            <FormField label="Photo (URL)" hint="Vide = initiale du nom">
              <Input value={tForm.avatar || ''} onChange={e => tf('avatar', e.target.value)} placeholder="https://…" />
            </FormField>
            <div style={{ gridColumn: '1/-1' }}>
              <FormField label="Témoignage" required>
                <Textarea rows={4} value={tForm.text} onChange={e => tf('text', e.target.value)} placeholder="Décrivez votre expérience…" />
              </FormField>
            </div>
            <FormField label="Ordre d'affichage" hint="Plus petit = en premier">
              <Input type="number" value={tForm.sort_order || 0} onChange={e => tf('sort_order', parseInt(e.target.value) || 0)} />
            </FormField>
            <FormField label="Statut">
              <Select value={tForm.is_active ? '1' : '0'} onChange={e => tf('is_active', e.target.value === '1')}>
                <option value="1">Visible sur le site</option>
                <option value="0">Masqué</option>
              </Select>
            </FormField>
          </div>
          {/* Aperçu */}
          {(tForm.name || tForm.text) && (
            <div style={{ marginTop: 'var(--space-md)', padding: 'var(--space-md)', background: 'var(--bg-page)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>Aperçu</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--brand-accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0, overflow: 'hidden' }}>
                  {tForm.avatar ? <img src={tForm.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (tForm.name?.[0] || '?')}
                </div>
                <div>
                  <div style={{ color: '#f59e0b', fontSize: 13, letterSpacing: 1, marginBottom: 4 }}>{'★'.repeat(tForm.rating || 5)}</div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', margin: '0 0 6px' }}>"{tForm.text || '…'}"</p>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{tForm.name || '…'}{tForm.company ? ` — ${tForm.company}` : ''}</div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* ── Confirmation suppression ── */}
      {confirmT && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', maxWidth: 420, width: '100%', margin: 'var(--space-md)' }}>
            <h3 style={{ marginBottom: 'var(--space-md)' }}>Supprimer l'avis</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>Cet avis sera définitivement supprimé du site.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setConfirmT(null)}>Annuler</Button>
              <Button variant="danger" onClick={() => deleteTestimonial(confirmT)}>Supprimer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PARAMÈTRES AMÉLIORÉS — coordonnées bancaires + moyens de paiement + super admin protégé
   ═══════════════════════════════════════════════════════════ */
export const AdminSettingsEnhancedPage = () => {
  const { user: currentUser } = useAuth();
  const { refresh: refreshSiteContext } = useSite();
  const [settings,    setSettings]    = useState({});
  const [users,       setUsers]       = useState([]);
  const [tab,         setTab]         = useState('general');
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [userModal,   setUserModal]   = useState(false);
  const [editUser,    setEditUser]    = useState(null);
  const [userForm,    setUserForm]    = useState({ first_name:'', last_name:'', email:'', password:'', role:'support', is_active:true });
  const [savingUser,  setSavingUser]  = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  // ── Maintenance ──
  const [maintConfirm, setMaintConfirm] = useState(false);

  // ── Popups ──
  const emptyPopup = { title:'', message:'', button_text:'', button_url:'', type:'info', show_once:true, is_active:true, start_date:'', end_date:'' };
  const [popups,       setPopups]       = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupModal,   setPopupModal]   = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [savingPopup,  setSavingPopup]  = useState(false);
  const [confirmPopup, setConfirmPopup] = useState(null);
  const [popupForm,    setPopupForm]    = useState(emptyPopup);
  const pf = (k, v) => setPopupForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    Promise.all([settingsAPI.all(), settingsAPI.users()])
      .then(([s, u]) => {
        const flat = {};
        Object.values(s.data.data || {}).forEach(arr => arr.forEach(item => {
          // Normalise les booléens PHP (true/false → '1'/'0') pour comparaisons cohérentes
          const val = item.value;
          flat[item.key] = typeof val === 'boolean' ? (val ? '1' : '0')
                         : typeof val === 'number'  ? String(val)
                         : val;
        }));

        // Valeurs par défaut pour les clés qui peuvent être absentes de la DB
        const defaults = {
          homepage_show_techniques:    '1',
          homepage_show_testimonials:  '1',
          homepage_testimonials_title: 'Ils nous font confiance',
        };
        setSettings({ ...defaults, ...flat });
        setUsers(u.data.data || []);
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await settingsAPI.update(settings); toast.success('Paramètres sauvegardés ✓'); }
    catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const saveUser = async () => {
    setSavingUser(true);
    try {
      if (editUser) await settingsAPI.updateUser(editUser.id, userForm);
      else          await settingsAPI.createUser(userForm);
      toast.success(editUser ? 'Utilisateur mis à jour ✓' : 'Utilisateur créé ✓');
      setUserModal(false);
      settingsAPI.users().then(({ data }) => setUsers(data.data || []));
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSavingUser(false); }
  };

  const deleteUser = async (id) => {
    try {
      await settingsAPI.deleteUser(id);
      toast.success('Utilisateur supprimé');
      settingsAPI.users().then(({ data }) => setUsers(data.data || []));
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    setConfirmUser(null);
  };

  // ── Maintenance ──
  const toggleMaintenance = async (enable) => {
    try {
      await settingsAPI.update({ maintenance_mode: enable ? '1' : '0' });
      setSettings(p => ({ ...p, maintenance_mode: enable ? '1' : '0' }));
      refreshSiteContext(); // Propage immédiatement au MaintenanceGuard
      toast.success(enable ? 'Mode maintenance activé' : 'Site remis en ligne ✓');
    } catch { toast.error('Erreur'); }
    setMaintConfirm(false);
  };

  // ── Popups ──
  const loadPopups = () => {
    setPopupLoading(true);
    popupAPI.listAll()
      .then(({ data }) => setPopups(data.data || []))
      .catch(() => {})
      .finally(() => setPopupLoading(false));
  };
  useEffect(() => { if (tab === 'popups') loadPopups(); }, [tab]); // eslint-disable-line

  const openPopupModal = (p = null) => {
    setEditingPopup(p);
    setPopupForm(p ? { title: p.title, message: p.message, button_text: p.button_text||'', button_url: p.button_url||'', type: p.type, show_once: !!p.show_once, is_active: !!p.is_active, start_date: p.start_date||'', end_date: p.end_date||'' } : emptyPopup);
    setPopupModal(true);
  };

  const savePopup = async () => {
    if (!popupForm.title || !popupForm.message) { toast.error('Titre et message requis'); return; }
    setSavingPopup(true);
    try {
      const d = { ...popupForm, start_date: popupForm.start_date || null, end_date: popupForm.end_date || null };
      if (editingPopup) await popupAPI.update(editingPopup.id, d);
      else              await popupAPI.create(d);
      toast.success(editingPopup ? 'Popup mis à jour ✓' : 'Popup créé ✓');
      setPopupModal(false);
      loadPopups();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSavingPopup(false); }
  };

  const deletePopup = async (id) => {
    try { await popupAPI.delete(id); toast.success('Popup supprimé'); loadPopups(); }
    catch { toast.error('Erreur'); }
    setConfirmPopup(null);
  };

  // Helper si() — pas un composant, évite la perte de focus
  const si = (k, label, type = 'text', hint, placeholder) => (
    <FormField label={label} hint={hint}>
      {type === 'textarea'
        ? <Textarea value={settings[k] || ''} onChange={e => setSettings(p => ({ ...p, [k]: e.target.value }))} rows={6} placeholder={placeholder} />
        : <Input type={type} value={settings[k] || ''} onChange={e => setSettings(p => ({ ...p, [k]: e.target.value }))} placeholder={placeholder} />
      }
    </FormField>
  );

  const roleLabels = { super_admin: 'Super Admin', admin: 'Admin', collaborator: 'Collaborateur', support: 'Support' };
  const roleBadge  = { super_admin: 'badge-error', admin: 'badge-accent', collaborator: 'badge-info', support: 'badge-neutral' };

  const tabs = [
    ['general','Général'],['homepage','Accueil'],['billing','Facturation'],
    ['banking','Coordonnées bancaires'],['payment_methods','Moyens de paiement'],
    ['email','Email'],['legal','Légal'],['social','Réseaux'],['popups','Popups'],['users','Utilisateurs']
  ];

  if (loading) return <div className="page-loader" style={{ flex: 1 }}><div className="spinner spinner-lg" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Paramètres</h1><p className="admin-page-subtitle">Configuration de votre site</p></div>
        {tab !== 'users' && tab !== 'popups' && <Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button>}
      </div>
      <div className="admin-page-content">
        <div style={{ display: 'flex', gap: 'var(--space-xl)', alignItems: 'flex-start' }}>
          {/* Tab nav */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200, flexShrink: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 8 }}>
            {tabs.map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} style={{ padding: '9px 14px', borderRadius: 'var(--radius-sm)', textAlign: 'left', fontSize: 13, fontWeight: 600, background: tab === key ? 'rgba(230,57,70,.07)' : 'transparent', color: tab === key ? 'var(--brand-accent)' : 'var(--text-secondary)', border: 'none', cursor: 'pointer', transition: 'all .15s' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>

            {tab === 'general' && (<>
              {/* ── Bloc maintenance ── */}
              <div className="admin-card" style={{ padding: 'var(--space-xl)', border: settings.maintenance_mode === '1' ? '1.5px solid #f59e0b' : '1.5px solid var(--border)', background: settings.maintenance_mode === '1' ? 'rgba(245,158,11,.04)' : 'var(--bg-card)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={settings.maintenance_mode === '1' ? '#f59e0b' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Mode maintenance</span>
                      {settings.maintenance_mode === '1' && <span className="badge badge-warning" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>Actif</span>}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                      {settings.maintenance_mode === '1'
                        ? 'Le site est verrouillé — seuls les administrateurs y ont accès.'
                        : 'Verrouillez le site pour le public pendant une maintenance.'}
                    </p>
                  </div>
                  <Button
                    variant={settings.maintenance_mode === '1' ? 'primary' : 'secondary'}
                    onClick={() => settings.maintenance_mode === '1' ? toggleMaintenance(false) : setMaintConfirm(true)}
                  >
                    {settings.maintenance_mode === '1' ? 'Remettre en ligne' : 'Activer la maintenance'}
                  </Button>
                </div>
                {settings.maintenance_mode === '1' && (
                  <div style={{ marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--border)' }}>
                    {si('maintenance_message', 'Message affiché aux visiteurs', 'text', 'Laissez vide pour le message par défaut')}
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="sm" variant="primary" loading={saving} onClick={save}>Enregistrer le message</Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                {si('site_name', 'Nom du site')}
                {si('site_tagline', 'Slogan')}
                {si('site_email', 'Email de contact', 'email')}
                {si('site_phone', 'Téléphone')}
                <div style={{ gridColumn: '1/-1' }}>{si('site_address', 'Adresse complète')}</div>
                {/* ── Section logos ── */}
                <div style={{ gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 'var(--space-md)', marginTop: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', textTransform: 'uppercase', letterSpacing: .5 }}>
                    Logos du site
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-md)' }}>
                    {/* Logo fond clair */}
                    <div>
                      <FormField label="Logo — fond clair" hint="Header scrollé, backoffice (fond blanc)">
                        <Input value={settings['site_logo']||''} onChange={e=>setSettings(p=>({...p,site_logo:e.target.value}))} placeholder="https://…/logo-dark.png"/>
                      </FormField>
                      {settings['site_logo'] && (
                        <div style={{ marginTop: 8, padding: 10, background: '#ffffff', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                          <img src={settings['site_logo']} alt="" style={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain' }} onError={e=>e.target.style.display='none'}/>
                        </div>
                      )}
                    </div>
                    {/* Logo fond sombre */}
                    <div>
                      <FormField label="Logo — fond sombre" hint="Header transparent, footer, emails (fond sombre)">
                        <Input value={settings['site_logo_dark']||''} onChange={e=>setSettings(p=>({...p,site_logo_dark:e.target.value}))} placeholder="https://…/logo-white.png"/>
                      </FormField>
                      {settings['site_logo_dark'] && (
                        <div style={{ marginTop: 8, padding: 10, background: '#1e293b', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                          <img src={settings['site_logo_dark']} alt="" style={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain' }} onError={e=>e.target.style.display='none'}/>
                        </div>
                      )}
                    </div>
                    {/* Logo backoffice */}
                    <div>
                      <FormField label="Logo — backoffice" hint="Sidebar admin et page de connexion">
                        <Input value={settings['site_logo_admin']||''} onChange={e=>setSettings(p=>({...p,site_logo_admin:e.target.value}))} placeholder="https://…/logo-admin.png"/>
                      </FormField>
                      {settings['site_logo_admin'] && (
                        <div style={{ marginTop: 8, padding: 10, background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                          <img src={settings['site_logo_admin']} alt="" style={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain' }} onError={e=>e.target.style.display='none'}/>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {si('site_favicon', 'Favicon (URL)', 'text', 'Icône onglet navigateur (32×32px)')}
              </div>
            </>)}

            {tab === 'homepage' && (
              <HomepageFullSection settings={settings} setSettings={setSettings} />
            )}

            {tab === 'billing' && (
              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                {si('vat_rate', 'Taux TVA (%)', 'number')}
                {si('currency_symbol', 'Symbole devise')}
                {si('quote_prefix', 'Préfixe devis', 'text', 'ex: DEV-')}
                {si('invoice_prefix', 'Préfixe facture', 'text', 'ex: FAC-')}
                {si('quote_validity_days', 'Validité devis (jours)', 'number')}
              </div>
            )}

            {tab === 'banking' && (
              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <div style={{ padding: 12, background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--info)' }}>
                  Ces informations apparaîtront sur vos devis et factures dans la section "Coordonnées bancaires".
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                  {si('bank_name', 'Nom de la banque', 'text', undefined, 'BNP Paribas')}
                  {si('bank_iban', 'IBAN', 'text', undefined, 'FR76 1234 5678 9012 3456 7890 123')}
                  {si('bank_bic', 'BIC / SWIFT', 'text', undefined, 'BNPAFRPPXXX')}
                  {si('bank_account_name', 'Titulaire du compte', 'text', undefined, 'Artimarq SARL')}
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  {si('bank_extra', 'Informations complémentaires', 'textarea', undefined, 'Ex: Toute commande est payable à 30 jours date de facture. Des pénalités de retard de 3× le taux légal seront appliquées.')}
                </div>
              </div>
            )}

            {tab === 'payment_methods' && (
              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <div style={{ padding: 12, background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--info)' }}>
                  Cochez les modes de paiement acceptés — ils apparaîtront sur vos devis et factures.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    ['payment_bank_transfer', 'Virement bancaire'],
                    ['payment_check',         'Chèque'],
                    ['payment_cash',          'Espèces'],
                    ['payment_card',          'Carte bancaire'],
                    ['payment_paypal',        'PayPal'],
                    ['payment_stripe',        'Stripe / CB en ligne'],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: `1.5px solid ${settings[key] === '1' ? 'var(--brand-accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', cursor: 'pointer', background: settings[key] === '1' ? 'rgba(230,57,70,.04)' : 'var(--bg-card)', transition: 'all .15s' }}>
                      <input type="checkbox" checked={settings[key] === '1'} onChange={e => setSettings(p => ({ ...p, [key]: e.target.checked ? '1' : '0' }))} />
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
                    </label>
                  ))}
                </div>
                {si('payment_conditions', 'Conditions de règlement (texte libre)', 'textarea', undefined, 'Ex: Paiement à 30 jours. Escompte de 2% pour paiement comptant. Pas d')}
              </div>
            )}

            {tab === 'email' && (
              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                {si('smtp_host', 'SMTP Host')}
                {si('smtp_port', 'SMTP Port', 'number')}
                {si('smtp_user', 'SMTP User')}
                <FormField label="SMTP Password">
                    <PasswordInput value={settings['smtp_pass'] || ''} onChange={e => setSettings(p => ({ ...p, smtp_pass: e.target.value }))} placeholder="Mot de passe SMTP" autoComplete="new-password" />
                  </FormField>
                {si('smtp_from_name', 'Nom expéditeur')}
                {si('smtp_from_email', 'Email expéditeur', 'email')}
              </div>
            )}

            {tab === 'legal' && (
              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                  {si('legal_company', 'Raison sociale')}
                  {si('legal_siret', 'SIRET')}
                  {si('legal_rcs', 'RCS')}
                  {si('legal_capital', 'Capital social')}
                  {si('legal_director', 'Directeur de publication')}
                  {si('legal_host', 'Hébergeur')}
                </div>
                {si('legal_content', 'Mentions légales (HTML)', 'textarea')}
                {si('cgv_content', 'CGV (HTML)', 'textarea')}
                {si('privacy_content', 'Protection des données (HTML)', 'textarea')}
              </div>
            )}

            {tab === 'social' && (
              <div className="admin-card" style={{ padding: 'var(--space-xl)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                {si('social_facebook', 'URL Facebook')}
                {si('social_instagram', 'URL Instagram')}
                {si('social_linkedin', 'URL LinkedIn')}
                {si('social_tiktok', 'URL TikTok')}
                {si('google_analytics_id', 'Google Analytics ID', 'text', 'G-XXXXXXXX')}
                {si('facebook_pixel_id', 'Facebook Pixel ID')}
              </div>
            )}

            {tab === 'popups' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <div className="admin-card" style={{ padding: 'var(--space-xl)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-lg)' }}>
                    <div>
                      <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 4px' }}>Popups d'information</h2>
                      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Affichez des messages importants aux visiteurs (CGV, mises à jour, promotions…).</p>
                    </div>
                    <Button variant="primary" size="sm" onClick={() => openPopupModal()}>+ Nouveau popup</Button>
                  </div>

                  {popupLoading ? (
                    <div className="page-loader" style={{ minHeight: 120 }}><div className="spinner" /></div>
                  ) : popups.length === 0 ? (
                    <EmptyState icon={_i(<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></>, 40)} title="Aucun popup" description="Créez votre premier message d'information." />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {popups.map(p => {
                        const typeColors = { info: '#3b82f6', warning: '#f59e0b', success: '#10b981' };
                        const color = typeColors[p.type] || typeColors.info;
                        return (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-page)', opacity: p.is_active ? 1 : 0.5 }}>
                            <div style={{ width: 4, height: 36, borderRadius: 2, background: color, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{p.title}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.message}</div>
                            </div>
                            <span className={`badge ${p.is_active ? 'badge-success' : 'badge-neutral'}`}>{p.is_active ? 'Actif' : 'Inactif'}</span>
                            {p.start_date && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.start_date}{p.end_date ? ` → ${p.end_date}` : ''}</span>}
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              <Button size="sm" variant="secondary" onClick={() => openPopupModal(p)}>{ICO.edit}</Button>
                              <Button size="sm" variant="danger" onClick={() => setConfirmPopup(p.id)}>{ICO.trash}</Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'users' && (
              <div className="admin-card">
                <div className="admin-card-header">
                  <h3 style={{ fontWeight: 700 }}>Utilisateurs ({users.length}) — 1 seul Super Admin autorisé</h3>
                  <Button variant="primary" size="sm" onClick={() => { setEditUser(null); setUserForm({ first_name:'', last_name:'', email:'', password:'', role:'support', is_active:true }); setUserModal(true); }}>+ Ajouter</Button>
                </div>
                <table className="data-table">
                  <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th>Dernière connexion</th><th>Statut</th><th>Actions</th></tr></thead>
                  <tbody>
                    {users.map(u => {
                      const isSuperAdmin = u.role === 'super_admin';
                      const isCurrentUser = u.id === currentUser?.id;
                      return (
                        <tr key={u.id} style={{ background: isSuperAdmin ? 'rgba(230,57,70,.02)' : '' }}>
                          <td style={{ fontWeight: 700 }}>
                            {u.first_name} {u.last_name}
                            {isSuperAdmin && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--brand-accent)', background: 'rgba(230,57,70,.1)', padding: '1px 6px', borderRadius: 4 }}>ROOT</span>}
                            {isCurrentUser && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted)' }}>(vous)</span>}
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{u.email}</td>
                          <td><span className={`badge ${roleBadge[u.role] || 'badge-neutral'}`}>{roleLabels[u.role] || u.role}</span></td>
                          <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('fr-FR') : 'Jamais'}</td>
                          <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-neutral'}`}>{u.is_active ? 'Actif' : 'Inactif'}</span></td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {/* Super admin : toutes les données modifiables sauf le rôle */}
                              <Button size="sm" variant="secondary" onClick={() => { setEditUser(u); setUserForm({ ...u, password: '' }); setUserModal(true); }}>
                                {ICO.edit} Modifier
                              </Button>
                              {/* Pas de suppression pour super_admin */}
                              {!isSuperAdmin && (
                                <Button size="sm" variant="ghost" onClick={() => setConfirmUser(u.id)} title="Supprimer">{ICO.trash}</Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={userModal} onClose={() => setUserModal(false)}
        title={editUser?.role === 'super_admin' ? 'Modifier le Super Admin' : editUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setUserModal(false)}>Annuler</Button><Button variant="primary" loading={savingUser} onClick={saveUser}>Enregistrer</Button></div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {editUser?.role === 'super_admin' && (
            <div style={{ padding: 12, background: 'rgba(230,57,70,.06)', border: '1px solid rgba(230,57,70,.2)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--brand-accent)' }}>
              Compte Super Admin — toutes les informations sont modifiables <strong>sauf le rôle</strong>, qui est permanent et unique.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Prénom"><Input value={userForm.first_name} onChange={e => setUserForm(p => ({ ...p, first_name: e.target.value }))} /></FormField>
            <FormField label="Nom"><Input value={userForm.last_name} onChange={e => setUserForm(p => ({ ...p, last_name: e.target.value }))} /></FormField>
            <FormField label="Email" required><Input type="email" value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} /></FormField>
            <FormField label={editUser ? 'Nouveau mot de passe' : 'Mot de passe'} required={!editUser} hint={editUser ? 'Laisser vide pour ne pas changer' : ''}>
              <PasswordInput value={userForm.password || ''} onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} placeholder={editUser ? 'Laisser vide pour ne pas changer' : 'Min. 8 caractères'} autoComplete="new-password" />
            </FormField>
            <FormField label="Téléphone">
              <Input value={userForm.phone || ''} onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))} placeholder="+33 6 …" />
            </FormField>
            <FormField label="Rôle" hint={editUser?.role === 'super_admin' ? 'Non modifiable' : ''}>
              {editUser?.role === 'super_admin' ? (
                /* Rôle super_admin : affiché en lecture seule, non modifiable */
                <div style={{ padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 14 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-accent)', background: 'rgba(230,57,70,.1)', padding: '2px 8px', borderRadius: 4 }}>Super Admin</span>
                  <span style={{ fontSize: 12 }}>— rôle verrouillé</span>
                </div>
              ) : (
                <Select value={userForm.role} onChange={e => setUserForm(p => ({ ...p, role: e.target.value }))}>
                  {Object.entries(roleLabels)
                    .filter(([v]) => v !== 'super_admin' || !users.some(u => u.role === 'super_admin' && u.id !== editUser?.id))
                    .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              )}
            </FormField>
            <FormField label="Statut" hint={editUser?.role === 'super_admin' ? 'Toujours actif' : ''}>
              {editUser?.role === 'super_admin' ? (
                /* Super admin — statut verrouillé à Actif */
                <div style={{ padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="badge badge-success">✓ Actif</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>— statut verrouillé</span>
                </div>
              ) : (
                <Select value={userForm.is_active ? '1' : '0'} onChange={e => setUserForm(p => ({ ...p, is_active: e.target.value === '1' }))}>
                  <option value="1">Actif</option><option value="0">Inactif</option>
                </Select>
              )}
            </FormField>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirmUser} onClose={() => setConfirmUser(null)} onConfirm={() => deleteUser(confirmUser)} title="Supprimer l'utilisateur" message="Cet utilisateur sera définitivement supprimé." confirmLabel="Supprimer" danger />

      {/* ── Confirmation maintenance ── */}
      <Modal isOpen={maintConfirm} onClose={() => setMaintConfirm(false)} title="Activer le mode maintenance ?"
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setMaintConfirm(false)}>Annuler</Button>
            <Button variant="danger" onClick={() => toggleMaintenance(true)}>Oui, activer la maintenance</Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <div style={{ padding: 14, background: 'rgba(245,158,11,.08)', border: '1px solid #fcd34d', borderRadius: 'var(--radius-md)', fontSize: 14, color: '#92400e', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {ICO.warning}
            <span>En activant ce mode, <strong>le site sera inaccessible au public</strong>. Seuls les administrateurs connectés pourront y accéder. Les visiteurs verront une page de maintenance.</span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>Vous pouvez désactiver le mode maintenance à tout moment depuis cet écran.</p>
        </div>
      </Modal>

      {/* ── Modal CRUD Popup ── */}
      <Modal isOpen={popupModal} onClose={() => setPopupModal(false)}
        title={editingPopup ? 'Modifier le popup' : 'Nouveau popup d\'information'}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setPopupModal(false)}>Annuler</Button>
            <Button variant="primary" loading={savingPopup} onClick={savePopup}>Enregistrer</Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <FormField label="Titre" required>
            <Input value={popupForm.title} onChange={e => pf('title', e.target.value)} placeholder="Mise à jour des CGV" />
          </FormField>
          <FormField label="Message" required hint="Texte affiché dans le popup">
            <Textarea value={popupForm.message} onChange={e => pf('message', e.target.value)} rows={4} placeholder="Nos conditions générales de vente ont été mises à jour…" />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Texte du bouton" hint="Laisser vide si pas de bouton">
              <Input value={popupForm.button_text} onChange={e => pf('button_text', e.target.value)} placeholder="Voir les CGV" />
            </FormField>
            <FormField label="Lien du bouton (URL)">
              <Input value={popupForm.button_url} onChange={e => pf('button_url', e.target.value)} placeholder="/cgv" />
            </FormField>
            <FormField label="Type">
              <Select value={popupForm.type} onChange={e => pf('type', e.target.value)}>
                <option value="info">Info (bleu)</option>
                <option value="warning">Avertissement (orange)</option>
                <option value="success">Succès (vert)</option>
              </Select>
            </FormField>
            <FormField label="Statut">
              <Select value={popupForm.is_active ? '1' : '0'} onChange={e => pf('is_active', e.target.value === '1')}>
                <option value="1">Actif — visible sur le site</option>
                <option value="0">Inactif</option>
              </Select>
            </FormField>
            <FormField label="Date de début" hint="Laisser vide pour toujours">
              <Input type="date" value={popupForm.start_date} onChange={e => pf('start_date', e.target.value)} />
            </FormField>
            <FormField label="Date de fin" hint="Laisser vide pour illimité">
              <Input type="date" value={popupForm.end_date} onChange={e => pf('end_date', e.target.value)} />
            </FormField>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!popupForm.show_once} onChange={e => pf('show_once', e.target.checked)} />
            Afficher une seule fois par visiteur (mémorisé dans le navigateur)
          </label>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirmPopup} onClose={() => setConfirmPopup(null)} onConfirm={() => deletePopup(confirmPopup)} title="Supprimer ce popup ?" message="Le popup sera définitivement supprimé." confirmLabel="Supprimer" danger />
    </div>
  );
};
/* ═══════════════════════════════════════════════════════════
   PRESTATIONS — catalogue de lignes pré-remplies pour devis/factures
   ═══════════════════════════════════════════════════════════ */
export const AdminServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const empty = { name:'', description:'', unit_price:'', unit:'unité', category:'', tax_rate:20, is_active:true, sort_order:0 };
  const [form, setForm] = useState(empty);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = () => {
    setLoading(true);
    servicesAPI.listAll().then(({ data }) => setServices(data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setModal(true); };

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editing) await servicesAPI.update(editing.id, form);
      else         await servicesAPI.create(form);
      toast.success(editing ? 'Prestation mise à jour ✓' : 'Prestation créée ✓');
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await servicesAPI.delete(id); toast.success('Prestation supprimée'); load(); }
    catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  const categories = [...new Set(services.map(s => s.category).filter(Boolean))];

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Prestations</h1>
          <p className="admin-page-subtitle">Catalogue de lignes pré-remplies pour vos devis et factures</p>
        </div>
        <Button variant="primary" onClick={openNew}>+ Nouvelle prestation</Button>
      </div>

      <div className="admin-page-content">
        <div className="admin-card">
          {loading ? <div className="page-loader"><div className="spinner" /></div> :
            !services.length ? <EmptyState icon="🛠" title="Aucune prestation" description="Créez des lignes types pour les insérer rapidement dans vos devis et factures." action={<Button variant="primary" onClick={openNew}>Créer une prestation</Button>}/> :
            <table className="data-table">
              <thead><tr><th>Nom</th><th>Description</th><th>Catégorie</th><th>Prix HT</th><th>Unité</th><th>TVA</th><th>Statut</th><th></th></tr></thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight:700 }}>{s.name}</td>
                    <td style={{ fontSize:13, color:'var(--text-muted)', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.description || '—'}</td>
                    <td style={{ fontSize:12 }}>{s.category || '—'}</td>
                    <td style={{ fontWeight:700, color:'var(--brand-accent)' }}>{parseFloat(s.unit_price||0).toFixed(2)} €</td>
                    <td style={{ fontSize:13 }}>{s.unit}</td>
                    <td style={{ fontSize:13 }}>{s.tax_rate}%</td>
                    <td><span className={`badge ${s.is_active ? 'badge-success' : 'badge-neutral'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(s)}>✏️</Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirm(s.id)}>🗑</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Modifier la prestation' : 'Nouvelle prestation'} size="lg"
        footer={<div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button></div>}
      >
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--space-md)' }}>
          <div style={{ gridColumn:'1/-1' }}>
            <FormField label="Nom de la prestation" required><Input value={form.name} onChange={e => f('name', e.target.value)} placeholder="Ex: Broderie logo — jusqu'à 5 000 pts" autoFocus /></FormField>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <FormField label="Description"><Textarea rows={2} value={form.description||''} onChange={e => f('description', e.target.value)} placeholder="Détails affichés dans la ligne du devis" /></FormField>
          </div>
          <FormField label="Prix unitaire HT (€)" required><Input type="number" step="0.01" value={form.unit_price} onChange={e => f('unit_price', e.target.value)} placeholder="0.00" /></FormField>
          <FormField label="Unité"><Input value={form.unit||'unité'} onChange={e => f('unit', e.target.value)} placeholder="unité, heure, forfait…" /></FormField>
          <FormField label="Catégorie" hint="Pour organiser votre catalogue">
            <Input value={form.category||''} onChange={e => f('category', e.target.value)} placeholder="Broderie, Impression, Sérigraphie…" list="svc-cats" />
            <datalist id="svc-cats">{categories.map(c => <option key={c} value={c} />)}</datalist>
          </FormField>
          <FormField label="Taux TVA (%)"><Input type="number" step="0.1" value={form.tax_rate} onChange={e => f('tax_rate', e.target.value)} /></FormField>
          <FormField label="Ordre d'affichage"><Input type="number" value={form.sort_order||0} onChange={e => f('sort_order', parseInt(e.target.value)||0)} /></FormField>
          <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:24 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:14 }}>
              <input type="checkbox" checked={!!form.is_active} onChange={e => f('is_active', e.target.checked)} />
              Prestation active
            </label>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer la prestation" message="Cette prestation sera supprimée. Les devis existants ne sont pas affectés." confirmLabel="Supprimer" danger />
    </div>
  );
};
